import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  motion, AnimatePresence, MotionConfig,
  useScroll, useTransform, useReducedMotion,
} from "framer-motion";
import {
  Calendar, MapPin, Users, Globe, Trophy, Coffee,
  Sparkles, Heart, Mic, Code, ArrowUpRight, Play, X, CalendarDays,
  ChevronLeft, ChevronRight, Sun, Snowflake, Leaf, Flower2,
} from "lucide-react";
import PageBanner from "../PageBanner";

/* ───────────────────────── DATA (unchanged) ───────────────────────── */
const eventsData = {
  previous: [
    { id: 1, title: "TECHRESOLVE CHALLENGE 3.0", type: "Hackaton", date: "2026-03-16", location: "ISIMS, Sfax", description: "The third edition of the TechResolve Challenge united innovators and developers in a National Technical Hackathon dedicated to solving global challenges through IT-driven solutions. Through collaboration, interactive workshops, and team-building activities, participants fostered creativity, expanded their networks, and delivered impactful technological innovations in an inspiring and friendly atmosphere.", image: "/asset/event/trc-3.webp", highlights: ["Hackaton", "Challenges", "Surprises", "Networking"], category: "Hackaton", attendees: 250, websiteUrl: "https://techresolve-challenge.com" },
    { id: 2, title: "IEEE ISIMS SB 6TH ANNIVERSARY", type: "Anniversary", date: "2026-02-18", location: "ISIMS, Sfax", description: "6 years, countless memories. From a small beginning to a thriving community, IEEE ISIMS SB has become a family of dreamers and changemakers. Every moment, every smile, and every challenge have shaped who we are today.", image: "/asset/event/anniv6.webp", highlights: ["Celebration", "Awards", "Networking", "Live Music"], category: "Anniversary", videoUrl: "https://www.instagram.com/reel/DVjncYDDNeN/", attendees: 80 },
    { id: 14, title: "EduBridge", type: "Celebration", date: "2026-04-12", location: "ENIS", description: "A dynamic gathering celebrating Education Week through insightful panel discussions, hands-on workshops, and a vibrant atmosphere of learning, collaboration, and community.", image: "/asset/event/edubridge.webp", highlights: ["Discussion", "Workshop", "Education"], category: "Social", attendees: 30 },
    { id: 4, title: "Building Applications with Prompt Engineering", type: "Certification", date: "2026-02-28", location: "ISIMS, Sfax", description: "A successful NVIDIA certification session where participants explored prompt engineering and gained hands-on experience in building AI-powered applications.", image: "/asset/event/nvidiaa.webp", highlights: ["NVIDIA Certification", "Prompt Engineering", "Hands-on Labs"], category: "Technical", attendees: 50 },
    { id: 3, title: "TSYP13 National Congress", type: "Congress", date: "2025-12-22", endDate: "2025-12-24", location: "Hammamet, Tunisia", description: "The 13th Tunisian Students and Young Professionals Congress brought together young innovators for three days of workshops, competitions, and networking. A landmark event in Tunisian tech history.", image: "/heroieee1.webp", highlights: ["Awards Ceremony", "Workshops", "Networking", "Keynotes"], category: "Congress", attendees: 1200, videoUrl: "https://www.instagram.com/reel/DSxxKMyDOGd/" },
    { id: 5, title: "IEEE PLAY ON", type: "Celebration", date: "2026-04-05", location: "Touta Zoo Park", description: "Where passion meets performance : Celebrating strength, energy, and innovation this International Sports Day ", image: "/asset/event/touta.webp", highlights: ["Celebration", "Fun", "Sports Activities"], category: "Celebration", attendees: 28 },
    { id: 6, title: "IEEE Xtreme 19.0", type: "Competition", date: "2025-10-24", endDate: "2025-10-25", location: "Iyed Colab Sfax", description: "24-hour global coding marathon. Teams competed non-stop, solving complex algorithmic challenges. Pizza, caffeine, and code fueled the night.", image: "/asset/event/xtreme19.webp", highlights: ["24-hour Coding", "Global Competition", "Team Building", "Prizes"], category: "Technical", videoUrl: "https://www.instagram.com/reel/DQNdQo-kb21/", attendees: 100 },
    { id: 7, title: "IEEE DAY 2025 Celebration", type: "Celebration", date: "2025-10-15", location: "IIT, Tunisia", description: "Over 165 attendees from IEEE ISIMS, IEEE ENIS, and IEEE IIT Student Branches came together for an unforgettable evening of inspiration and connection.", image: "/asset/event/ieeeday.webp", highlights: ["Keynote", "Panel", "Team Building", "After Party"], category: "Social", videoUrl: "https://www.instagram.com/reel/DR2m1-ojGcT/", attendees: 170 },
    { id: 8, title: "Code of Ethics Session", type: "Workshop", date: "2025-11-26", location: "ISIMS, Sfax", description: "Interactive session introducing the IEEE Code of Ethics with Mentimeter quiz and real-world case studies.", image: "/asset/event/codethics.webp", highlights: ["Interactive", "Live Quiz", "Case Studies", "Certificates"], category: "Social", attendees: 45 },
    { id: 9, title: "StepIn IEEE Volunteering 2026", type: "Session", date: "2025-11-19", location: "ISIMS, Sfax", description: "First session introducing members to upcoming volunteering opportunities and leadership roles within IEEE.", image: "/asset/event/volon.webp", highlights: ["Leadership Training", "Volunteering", "Networking"], category: "Social", attendees: 75 },
    { id: 10, title: "AIoT Forum : Ideathon", type: "Forum", date: "2025-10-05", location: "ISIMS, Sfax", description: "Hybrid event exploring AIoT applications in smart cities, industry 4.0, and home automation.", image: "/asset/event/iot.webp", highlights: ["Bootcamp", "Visits", "Panel", "Pitch Competition"], category: "Technical", attendees: 50 },
    { id: 11, title: "Pre-Xtreme 19.0 Training", type: "Training", date: "2025-10-11", location: "ISIMS, Sfax", description: "Intensive problem-solving session covering algorithms, data structures, and competitive programming strategies.", image: "/asset/event/cstraining.webp", highlights: ["Algorithms", "Data Structures", "Mock Competition"], category: "Technical", attendees: 35 },
    { id: 12, title: "Industrial Visit to CRNS", type: "Visit", date: "2025-10-08", location: "CRNS, Sfax", description: "Exclusive tour of the Digital Research Center, exploring cutting-edge AI research and development.", image: "/asset/event/crns.webp", highlights: ["AI Research", "Lab Tour", "Expert Talks"], category: "Social", attendees: 40 },
    { id: 13, title: "Industrial Visit to SNA", type: "Visit", date: "2025-10-01", location: "SNA Poulina, Tunisia", description: "Behind-the-scenes tour of automated production lines and digital supply chain management.", image: "/asset/event/sna.webp", highlights: ["Factory Tour", "Automation", "Industry 4.0"], category: "Social", attendees: 35 },
    { id: 14, title: "IEEE DAY 2026 Celebration", type: "Celebration", date: "2026-10-15", location: "FSEG Sfax", description: "Throwback to our IEEE Day Celebration. This year it was truly unforgettable. It reminded us of how close-knit and wonderful the IEEE community is. From an insightful session on IEEE benefits to plenty of networking and fun, it was a day worth remembering.", image: "/asset/event/ieeeday2026.jpg", highlights: ["Team Building", "Celebration", "Keynote"], category: "Social", attendees: 200 },
  ],
};

/* ───────────────────────── HELPERS ───────────────────────── */
const EASE = [0.16, 1, 0.3, 1];

const getEventIcon = (type) => {
  switch (type?.toLowerCase()) {
    case "conference": return <Globe className="w-3.5 h-3.5" />;
    case "workshop": return <Mic className="w-3.5 h-3.5" />;
    case "bootcamp": return <Code className="w-3.5 h-3.5" />;
    case "competition": return <Trophy className="w-3.5 h-3.5" />;
    case "hackathon": case "hackaton": return <Coffee className="w-3.5 h-3.5" />;
    case "anniversary": return <Sparkles className="w-3.5 h-3.5" />;
    case "celebration": return <Heart className="w-3.5 h-3.5" />;
    default: return <Calendar className="w-3.5 h-3.5" />;
  }
};

const toDate = (s) => new Date(`${s}T00:00:00`);
const formatDate = (dateStr) => {
  const d = toDate(dateStr);
  return isNaN(d) ? dateStr : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
const dayOf = (s) => toDate(s).getDate();
const monShort = (s) => toDate(s).toLocaleDateString("en-US", { month: "short" }).toUpperCase();

/* Seasons: Winter = Dec–Feb, Spring = Mar–May, Summer = Jun–Aug, Autumn = Sep–Nov.
   December belongs to the NEXT year's winter (Dec 2025 → Winter 2026). */
const SEASONS = ["Winter", "Spring", "Summer", "Autumn"];
const SEASON_ICON = { Winter: Snowflake, Spring: Flower2, Summer: Sun, Autumn: Leaf };
const seasonOf = (s) => {
  const d = toDate(s);
  const m = d.getMonth();
  let name, year = d.getFullYear();
  if (m === 11) { name = "Winter"; year += 1; }
  else if (m <= 1) name = "Winter";
  else if (m <= 4) name = "Spring";
  else if (m <= 7) name = "Summer";
  else name = "Autumn";
  return { name, year, key: `${year}-${SEASONS.indexOf(name)}` };
};

// The size of the event decides how loud its card is
const tierOf = (e) => (e.attendees >= 200 ? "xl" : e.attendees >= 100 ? "l" : e.attendees >= 45 ? "m" : "s");

const groupByTier = (events) => {
  const groups = [];
  events.forEach((e) => {
    const t = tierOf(e);
    const last = groups[groups.length - 1];
    if (last && last.tier === t) last.items.push(e);
    else groups.push({ tier: t, items: [e] });
  });
  return groups;
};

const openProps = (onOpen, event) => ({
  role: "button",
  tabIndex: 0,
  onClick: () => onOpen(event),
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(event); }
  },
});

/* ───────────────────────── SMALL PIECES ───────────────────────── */
function LatestTag({ onDark = false }) {
  return (
    <span className={`inline-flex items-center gap-2 text-[10px] font-bold px-3 py-1.5 ${onDark ? "bg-white text-[#00334d]" : "bg-[#006699]/10 text-[#006699]"}`}>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#006699] opacity-70 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#006699]" />
      </span>
      Latest
    </span>
  );
}

function CategoryChip({ category, dark = true }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1.5 ${dark ? "bg-[#006699] text-white" : "bg-[#006699]/10 text-[#006699]"}`}>
      {getEventIcon(category)} {category}
    </span>
  );
}

function Meta({ event, light = false }) {
  const c = light ? "text-white/80" : "text-[#00334d]/60";
  const i = light ? "text-[#7dd3fc]" : "text-[#006699]";
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-1 text-xs sm:text-sm font-semibold ${c}`}>
      <span className="flex items-center gap-1.5"><Calendar className={`w-4 h-4 ${i}`} />{formatDate(event.date)}{event.endDate && ` – ${formatDate(event.endDate)}`}</span>
      <span className="flex items-center gap-1.5"><MapPin className={`w-4 h-4 ${i}`} />{event.location}</span>
    </div>
  );
}

function PlayLink({ url, className = "" }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      aria-label="Watch recap"
      className={`inline-flex items-center justify-center bg-white text-[#006699] hover:bg-[#7dd3fc] hover:text-[#00334d] transition-colors ${className}`}
    >
      <Play className="w-4 h-4 fill-current ml-0.5" />
    </a>
  );
}

/* ───────────────────────── 1. SPOTLIGHT (big events) ───────────────────────── */
function Spotlight({ event, onOpen, latest }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-8%", "8%"]);

  return (
    <motion.article
      ref={ref}
      {...openProps(onOpen, event)}
      initial={{ opacity: 0, clipPath: "inset(10% 5% 10% 5%)" }}
      whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: EASE }}
      className="group relative cursor-pointer overflow-hidden bg-[#00334d] h-[460px] sm:h-[540px] lg:h-[620px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] focus-visible:ring-offset-2"
    >
      <motion.div style={{ y }} className="absolute -top-[10%] left-0 w-full h-[120%]">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#00334d] via-[#00334d]/55 to-[#00334d]/10" />

      <span className="absolute top-4 left-4 w-10 h-10 border-t-2 border-l-2 border-[#7dd3fc] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <span className="absolute bottom-4 right-4 w-10 h-10 border-b-2 border-r-2 border-[#7dd3fc] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="absolute top-5 left-5 sm:top-8 sm:left-8 flex flex-wrap items-center gap-2">
        <CategoryChip category={event.category} />
        {latest && <LatestTag onDark />}
      </div>

      <div className="absolute top-12 right-5 sm:top-6 sm:right-8 text-right pointer-events-none">
        <span
          className="block text-6xl sm:text-8xl lg:text-9xl font-black leading-none tracking-tighter text-transparent tabular-nums"
          style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.85)" }}
        >
          {event.attendees}+
        </span>
        <span className="text-white/80 text-xs sm:text-sm font-semibold">attendees</span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 lg:p-12">
        <Meta event={event} light />
        <h3 className="mt-3 text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.02] tracking-tight max-w-4xl">
          {event.title}
        </h3>
        <p className="mt-4 text-sm sm:text-base text-white/75 leading-relaxed max-w-2xl line-clamp-2 sm:line-clamp-3">
          {event.description}
        </p>
        <div className="mt-5 flex items-end justify-between gap-4">
          <div className="hidden sm:flex flex-wrap gap-2">
            {event.highlights?.slice(0, 4).map((h) => (
              <span key={h} className="text-xs font-semibold text-white/85 border border-white/30 px-3 py-1">#{h}</span>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <PlayLink url={event.videoUrl} className="w-11 h-11" />
            <span className="inline-flex items-center gap-2 h-11 px-5 bg-white text-[#00334d] text-sm font-bold group-hover:bg-[#7dd3fc] transition-colors">
              Open <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* ───────────────────────── 2. SPLIT (strong events) ───────────────────────── */
function Split({ event, reverse, onOpen, latest }) {
  return (
    <article
      {...openProps(onOpen, event)}
      className="group grid grid-cols-1 lg:grid-cols-12 gap-7 lg:gap-10 items-center cursor-pointer focus:outline-none"
    >
      <motion.div
        initial={{ opacity: 0, x: reverse ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className={`lg:col-span-7 relative ${reverse ? "lg:order-2" : ""}`}
      >
        <span className={`absolute inset-0 border-2 border-[#006699] transition-transform duration-500 ${reverse ? "-translate-x-3 translate-y-3" : "translate-x-3 translate-y-3"} group-hover:translate-x-0 group-hover:translate-y-0`} />
        <div className="relative overflow-hidden aspect-[4/3] bg-[#00334d]">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
          {event.videoUrl && <PlayLink url={event.videoUrl} className="absolute bottom-4 left-4 w-11 h-11" />}
        </div>
        <div className={`absolute -top-4 ${reverse ? "-right-2 sm:-right-4" : "-left-2 sm:-left-4"} bg-[#00334d] text-white px-4 py-3 shadow-xl`}>
          <span className="block text-3xl sm:text-4xl font-black leading-none tabular-nums">{event.attendees}+</span>
          <span className="block text-[11px] text-white/70 font-semibold mt-1">attendees</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
        className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <CategoryChip category={event.category} dark={false} />
          {latest && <LatestTag />}
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#00334d] leading-[1.05] tracking-tight group-hover:text-[#006699] transition-colors">
          {event.title}
        </h3>
        <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed line-clamp-4">{event.description}</p>
        <div className="mt-5"><Meta event={event} /></div>
        <span className="mt-6 inline-flex items-center gap-3 text-sm font-bold text-[#006699]">
          <span className="h-px w-8 bg-[#006699] transition-all duration-500 group-hover:w-16" />
          Open event <ArrowUpRight className="w-4 h-4" />
        </span>
      </motion.div>
    </article>
  );
}

/* ───────────────────────── 3. POSTER (medium events) ───────────────────────── */
function Poster({ event, index, onOpen, latest }) {
  return (
    <motion.article
      {...openProps(onOpen, event)}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: EASE }}
      className="group relative cursor-pointer overflow-hidden bg-[#00334d] aspect-[3/4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] focus-visible:ring-offset-2"
    >
      <img src={event.image} alt={event.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#00334d] via-[#00334d]/45 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />

      <div className="absolute top-0 left-0 bg-white text-center w-14 py-2.5 shadow-lg">
        <span className="block text-2xl font-black text-[#00334d] leading-none">{dayOf(event.date)}</span>
        <span className="block text-[10px] font-bold text-[#006699] mt-1">{monShort(event.date)}</span>
      </div>
      <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
        <CategoryChip category={event.category} />
        {latest && <LatestTag onDark />}
      </div>

      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="text-xl sm:text-2xl font-black text-white leading-tight">{event.title}</h3>
        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] [@media(hover:none)]:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
          <div className="overflow-hidden">
            <p className="mt-3 text-sm text-white/80 leading-relaxed line-clamp-3">{event.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {event.highlights?.slice(0, 3).map((h) => (
                <span key={h} className="text-[11px] font-semibold text-white/85 border border-white/30 px-2 py-0.5">#{h}</span>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs font-semibold text-white/80">
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#7dd3fc]" />{event.location}</span>
              <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#7dd3fc]" />{event.attendees}+</span>
            </div>
          </div>
        </div>
      </div>
      <PlayLink url={event.videoUrl} className="absolute bottom-5 right-5 w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity [@media(hover:none)]:hidden" />
    </motion.article>
  );
}

/* ───────────────────────── 4. STRIP (small events) ───────────────────────── */
function Strip({ event, index, onOpen, latest }) {
  return (
    <motion.li
      {...openProps(onOpen, event)}
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: EASE }}
      className="group relative overflow-hidden border-t border-[#006699]/15 last:border-b cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#006699]"
    >
      <span className="absolute inset-0 bg-[#00334d] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
      <div className="relative flex items-center gap-4 sm:gap-6 py-4 px-3 sm:px-5">
        <div className="w-12 text-center flex-shrink-0">
          <span className="block text-2xl font-black text-[#00334d] group-hover:text-white transition-colors leading-none">{dayOf(event.date)}</span>
          <span className="block text-[11px] font-bold text-[#006699] group-hover:text-[#7dd3fc] transition-colors mt-1">{monShort(event.date)}</span>
        </div>
        <img
          src={event.image}
          alt=""
          loading="lazy"
          className="w-20 h-14 sm:w-28 sm:h-[72px] object-cover flex-shrink-0 grayscale group-hover:grayscale-0 transition-all duration-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-base sm:text-lg font-bold text-[#00334d] group-hover:text-white transition-colors truncate">{event.title}</h4>
            {latest && <span className="hidden sm:block flex-shrink-0"><LatestTag /></span>}
          </div>
          <p className="mt-0.5 text-xs sm:text-sm text-slate-500 group-hover:text-white/70 transition-colors flex items-center gap-1.5 truncate">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />{event.location}
            <span className="opacity-50">·</span>{event.category}
          </p>
        </div>
        <ArrowUpRight className="w-5 h-5 flex-shrink-0 text-[#006699] group-hover:text-white transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </div>
    </motion.li>
  );
}

/* ───────────────────────── MODAL (same lightbox style as Awards) ─────────────────────────
   Fixed to the screen: gray transparent backdrop, X to close, arrows to browse.
   Desktop = horizontal (image left, details right). Phone = vertical (image on top).
   The card never grows past the screen; only the details panel scrolls inside it. */
function EventModal({ event, index, total, onClose, onPrev, onNext }) {
  const reduce = useReducedMotion();
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onNext, onPrev]);

  const swipe = (_, info) => {
    if (info.offset.x < -60) onNext();
    else if (info.offset.x > 60) onPrev();
  };

  const navBtn =
    "w-11 h-11 bg-white/10 hover:bg-white/25 text-white border border-white/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={event.title}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 lg:px-24 lg:py-8"
      onClick={onClose}
    >
      {/* close */}
      <button
        ref={closeRef}
        onClick={onClose}
        aria-label="Close"
        className={`${navBtn} absolute top-3 right-3 sm:top-6 sm:right-6 z-20`}
      >
        <X className="w-5 h-5" />
      </button>

      {/* desktop arrows sit on the screen edges, like the awards lightbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous event"
        className={`${navBtn} hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-20`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next event"
        className={`${navBtn} hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-20`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* the card: fixed to the viewport height, never pushes the page */}
      <div
        className="relative w-full max-w-6xl h-[calc(100dvh-5.5rem)] sm:h-[calc(100dvh-7rem)] lg:h-[min(76vh,680px)] mt-10 sm:mt-6 lg:mt-0 bg-white shadow-[0_30px_80px_rgba(0,0,0,0.5)] flex flex-col lg:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── image (top on phone, left on desktop) ── */}
        <div className="relative bg-[#00334d] overflow-hidden h-[34%] sm:h-[40%] lg:h-full lg:w-[58%] flex-shrink-0">
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
          <span className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-[#7cc4e8] z-20 pointer-events-none" />
          <span className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-[#7cc4e8] z-20 pointer-events-none" />

          <AnimatePresence mode="wait">
            <motion.div
              key={event.id}
              initial={reduce ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.25}
              onDragEnd={swipe}
              style={{ touchAction: "pan-y" }}
              className="absolute inset-0 flex items-center justify-center p-6 sm:p-8 lg:p-10 z-10"
            >
              <img
                src={event.image}
                alt={event.title}
                className="max-w-full max-h-full object-contain shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-white/20"
                draggable={false}
              />
            </motion.div>
          </AnimatePresence>

          {/* phone arrows live on the image */}
          <button
            onClick={onPrev}
            aria-label="Previous event"
            className="lg:hidden absolute left-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 bg-[#00334d]/80 text-white border border-white/20 flex items-center justify-center active:bg-[#006699]"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={onNext}
            aria-label="Next event"
            className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 bg-[#00334d]/80 text-white border border-white/20 flex items-center justify-center active:bg-[#006699]"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {event.videoUrl && (
            <a
              href={event.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 z-30 inline-flex items-center gap-2 h-9 px-3.5 bg-white text-[#006699] text-xs font-bold hover:bg-[#7dd3fc] hover:text-[#00334d] transition-colors"
            >
              <Play className="w-3.5 h-3.5 fill-current" /> Watch recap
            </a>
          )}
        </div>

        {/* ── details (bottom on phone, right on desktop). Only this scrolls. ── */}
        <div className="flex flex-col flex-1 min-h-0 bg-[#fafcff] border-t lg:border-t-0 lg:border-l border-[#006699]/10">
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 sm:p-7 lg:p-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={event.id}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-center gap-2 mb-3 text-[#006699]">
                  {getEventIcon(event.category)}
                  <span className="text-xs font-semibold">{event.category || event.type}</span>
                </div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-[#00334d] leading-tight">{event.title}</h2>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm text-slate-500 font-semibold">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-[#006699]" />
                    {formatDate(event.date)}{event.endDate && ` – ${formatDate(event.endDate)}`}
                  </span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#006699]" />{event.location}</span>
                  {event.attendees && (
                    <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#006699]" />{event.attendees}+ attendees</span>
                  )}
                </div>

                <p className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed max-w-[62ch]">{event.description}</p>

                {event.highlights?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {event.highlights.map((h) => (
                      <span key={h} className="text-xs font-semibold text-[#006699] bg-[#006699]/5 border border-[#006699]/15 px-3 py-1">#{h}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* footer stays pinned: counter + website button */}
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-7 lg:px-9 py-3.5 border-t border-[#006699]/10 bg-white">
            <span className="text-xs font-semibold text-[#00334d]/60 tabular-nums">
              {index + 1} of {total}
            </span>
            {event.websiteUrl && (
              <a
                href={event.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-5 bg-[#006699] hover:bg-[#00334d] text-white text-xs sm:text-sm font-bold transition-colors"
              >
                <Globe className="w-4 h-4" /> Visit website
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────────────── PAGE ───────────────────────── */
function SeasonLabel({ season, year, count }) {
  const Icon = SEASON_ICON[season];
  return (
    <div className="lg:sticky lg:top-28 self-start">
      <div className="flex items-center gap-3 lg:block">
        <Icon className="w-7 h-7 text-[#006699] lg:mb-3" strokeWidth={1.75} />
        <span
          className="block text-5xl sm:text-6xl lg:text-[44px] xl:text-5xl font-black leading-[0.9] tracking-tighter text-transparent uppercase"
          style={{ WebkitTextStroke: "1.5px #006699" }}
        >
          {season}
        </span>
        <span className="block text-2xl sm:text-3xl font-black text-[#00334d] leading-none lg:mt-2">{year}</span>
      </div>
      <span className="block mt-3 text-xs sm:text-sm font-semibold text-[#006699]">
        {count} {count === 1 ? "event" : "events"}
      </span>
    </div>
  );
}

export default function Events() {
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("All");

  const all = eventsData.previous;
  const categories = ["All", ...new Set(all.map((e) => e.category))];
  const filtered = selectedFilter === "All" ? all : all.filter((e) => e.category === selectedFilter);

  // Group by season, newest season first, newest event first inside it
  const seasons = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => toDate(b.date) - toDate(a.date));
    const map = new Map();
    sorted.forEach((e) => {
      const s = seasonOf(e.date);
      if (!map.has(s.key)) map.set(s.key, { key: s.key, season: s.name, year: s.year, events: [] });
      map.get(s.key).events.push(e);
    });
    return [...map.values()].sort((a, b) => {
      const [ya, sa] = a.key.split("-").map(Number);
      const [yb, sb] = b.key.split("-").map(Number);
      return yb - ya || sb - sa;
    });
  }, [filtered]);

  // Flat list in display order, used by the modal arrows
  const flat = useMemo(() => seasons.flatMap((s) => s.events), [seasons]);
  const latestId = flat[0]?.id;

  const selectedIndex = flat.findIndex((e) => e.id === selectedId);
  const selectedEvent = selectedIndex >= 0 ? flat[selectedIndex] : null;

  const open = useCallback((e) => setSelectedId(e.id), []);
  const close = useCallback(() => setSelectedId(null), []);
  const step = useCallback(
    (dir) => {
      if (!flat.length) return;
      const i = flat.findIndex((e) => e.id === selectedId);
      setSelectedId(flat[(i + dir + flat.length) % flat.length].id);
    },
    [flat, selectedId]
  );
  const onPrev = useCallback(() => step(-1), [step]);
  const onNext = useCallback(() => step(1), [step]);

  const renderGroup = (group, gi) => {
    const { tier, items } = group;

    if (tier === "xl") {
      return items.map((e) => <Spotlight key={e.id} event={e} onOpen={open} latest={e.id === latestId} />);
    }
    if (tier === "l" || (tier === "m" && items.length === 1)) {
      return items.map((e, i) => (
        <Split key={e.id} event={e} reverse={(gi + i) % 2 === 1} onOpen={open} latest={e.id === latestId} />
      ));
    }
    if (tier === "m") {
      return (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${items.length > 2 ? "lg:grid-cols-3" : ""} gap-5 lg:gap-6`}>
          {items.map((e, i) => (
            <Poster key={e.id} event={e} index={i} onOpen={open} latest={e.id === latestId} />
          ))}
        </div>
      );
    }
    return (
      <ul>
        {items.map((e, i) => (
          <Strip key={e.id} event={e} index={i} onOpen={open} latest={e.id === latestId} />
        ))}
      </ul>
    );
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-[#fafcff] relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: `repeating-linear-gradient(-45deg, rgba(15,105,157,0.02) 0px, rgba(15,105,157,0.02) 1px, transparent 1px, transparent 18px)` }}
        />

        <PageBanner
          title="Events"
          description="Explore all the workshops, hackathons, competitions, and gatherings organized by IEEE ISIMS Student Branch."
          Icon={CalendarDays}
        />

        {/* Filter bar */}
        <div className="relative z-10 bg-white border-y border-[#006699]/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 sm:py-5 flex items-center justify-between gap-6">
            <div
              role="tablist"
              aria-label="Filter events by category"
              className="flex items-center gap-1.5 overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {categories.map((cat) => {
                const on = selectedFilter === cat;
                const count = cat === "All" ? all.length : all.filter((e) => e.category === cat).length;
                return (
                  <button
                    key={cat}
                    role="tab"
                    aria-selected={on}
                    onClick={() => setSelectedFilter(cat)}
                    className={`relative flex-shrink-0 inline-flex items-center gap-2 h-10 pl-3.5 pr-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] focus-visible:ring-offset-2 ${
                      on ? "text-white" : "text-[#00334d]/70 hover:text-[#006699] hover:bg-[#006699]/5"
                    }`}
                  >
                    {on && (
                      <motion.span
                        layoutId="filter-pill"
                        className="absolute inset-0 bg-[#00334d]"
                        transition={{ type: "spring", stiffness: 420, damping: 36 }}
                      />
                    )}
                    <span className={`relative ${on ? "text-[#7dd3fc]" : "text-[#006699]"}`}>
                      {cat === "All" ? <CalendarDays className="w-3.5 h-3.5" /> : getEventIcon(cat)}
                    </span>
                    <span className="relative whitespace-nowrap">{cat}</span>
                    <span
                      className={`relative min-w-[22px] px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-center ${
                        on ? "bg-white/15 text-white" : "bg-[#006699]/10 text-[#006699]"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="hidden md:flex items-baseline gap-2 flex-shrink-0 text-sm font-semibold text-[#00334d]/60 tabular-nums">
              <span className="text-3xl font-black text-[#006699] leading-none">{filtered.length}</span>
              {filtered.length === 1 ? "event" : "events"}
            </p>
          </div>
        </div>

        {/* Timeline by season */}
        <main className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-6 md:py-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              {seasons.map((s, si) => (
                <section
                  key={s.key}
                  className={`grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-14 py-10 lg:py-14 ${si > 0 ? "border-t border-[#006699]/15" : ""}`}
                >
                  <SeasonLabel season={s.season} year={s.year} count={s.events.length} />
                  <div className="space-y-12 lg:space-y-16 min-w-0">
                    {groupByTier(s.events).map((g, gi) => (
                      <React.Fragment key={`${s.key}-${gi}`}>{renderGroup(g, gi)}</React.Fragment>
                    ))}
                  </div>
                </section>
              ))}
            </motion.div>
          </AnimatePresence>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500">No events found for this category.</p>
            </div>
          )}
        </main>

        <AnimatePresence>
          {selectedEvent && (
            <EventModal
              key="event-modal"
              event={selectedEvent}
              index={selectedIndex}
              total={flat.length}
              onClose={close}
              onPrev={onPrev}
              onNext={onNext}
            />
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
}